import React from "react";
import type { ShopEntity, ShopFooterDesign } from "../../../../../../domain/types/company-shop";
import { FOOTER_PRESETS, FOOTER_PRESET_LABELS, type FooterPresetKey } from "../../constants";
import { applyFooterPreset, patchFooterDesign } from "../../utils";
import { Field, SettingsCard, TextField, ToggleRow } from "../ui/SettingsUI";
import { MenuItemsEditor, ThemeFields } from "./DesignShared";

interface FooterSettingsProps {
  footerDesign: ShopFooterDesign;
  setFooterDesign: React.Dispatch<React.SetStateAction<ShopFooterDesign>>;
  shopEntityData: ShopEntity | null;
  setShopEntityData: React.Dispatch<React.SetStateAction<ShopEntity | null>>;
}

const PRESET_OPTIONS = (Object.keys(FOOTER_PRESETS) as FooterPresetKey[]).map((key) => ({
  value: key as string,
  label: FOOTER_PRESET_LABELS[key],
}));

type SnsField = "youtubeUrl" | "facebookUrl" | "instagramUrl" | "naverBlogUrl" | "kakaoChannelUrl";

const SNS_FIELDS: { field: SnsField; label: string; placeholder: string }[] = [
  { field: "instagramUrl", label: "인스타그램", placeholder: "https://instagram.com/..." },
  { field: "youtubeUrl", label: "유튜브", placeholder: "https://youtube.com/..." },
  { field: "facebookUrl", label: "페이스북", placeholder: "https://facebook.com/..." },
  { field: "naverBlogUrl", label: "네이버 블로그", placeholder: "https://blog.naver.com/..." },
  { field: "kakaoChannelUrl", label: "카카오 채널", placeholder: "https://pf.kakao.com/..." },
];

/** 푸터 탭: 테마(프리셋 · 색 · 표시 정보) + 메뉴 + SNS(노출 스위치와 주소를 한 카드에) */
export const FooterSettings = ({ footerDesign, setFooterDesign, shopEntityData, setShopEntityData }: FooterSettingsProps) => {
  const patch = (values: Partial<ShopFooterDesign>) => setFooterDesign((original) => patchFooterDesign(original, values));

  return (
    <>
      <SettingsCard title="테마" description="쇼핑몰 하단 영역의 색과 표시할 회사 정보입니다.">
        <ThemeFields
          presetKey={footerDesign.presetKey}
          presetOptions={footerDesign.presetKey === "CUSTOM" ? [...PRESET_OPTIONS, { value: "CUSTOM", label: "직접 설정" }] : PRESET_OPTIONS}
          onPreset={(presetKey) => {
            if (presetKey !== "CUSTOM") setFooterDesign((original) => applyFooterPreset(original, presetKey as FooterPresetKey));
          }}
          backgroundColor={footerDesign.backgroundColor}
          textColor={footerDesign.textColor}
          onColor={patch}
        />
        <ToggleRow
          label="사업자 정보"
          description="상호 · 대표 · 주소 · 사업자등록번호"
          checked={footerDesign.showBusinessInfo}
          onChange={(showBusinessInfo) => patch({ showBusinessInfo })}
        />
        <ToggleRow
          label="고객센터 · 영업시간"
          checked={footerDesign.showBusinessHours}
          onChange={(showBusinessHours) => patch({ showBusinessHours })}
        />
      </SettingsCard>

      <SettingsCard title="메뉴" description="문구를 고치고 스위치로 노출합니다.">
        <MenuItemsEditor items={footerDesign.menuItems} onChange={(menuItems) => patch({ menuItems })} />
      </SettingsCard>

      <SettingsCard
        title="SNS"
        description="주소를 넣은 SNS 만 아이콘으로 표시됩니다."
        toggle={{ checked: footerDesign.showSns, onChange: (showSns) => patch({ showSns }), label: "SNS 아이콘 노출" }}
      >
        {SNS_FIELDS.map(({ field, label, placeholder }) => (
          <Field key={field} label={label}>
            <TextField
              value={shopEntityData?.[field] ?? ""}
              placeholder={placeholder}
              onChange={(event) => {
                const value = event.target.value;
                setShopEntityData((original) => (original ? { ...original, [field]: value } : original));
              }}
            />
          </Field>
        ))}
      </SettingsCard>
    </>
  );
};
